/* eslint-disable react/display-name */
import React, { useContext, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '@/contexts/AuthContext';
import { Loader } from '@/layout/loader';
import { AccessDenied } from '@/layout/common-pages';

/**
 * Higher-Order Component that wraps a component with SOS authorization logic.
 * It checks if the current user has access to the route based on the menu paths
 * provided in the authorization context.
 *
 * @param {React.ComponentType<any>} WrappedComponent - The component to wrap with authorization logic.
 * @return {React.FC} A component that renders the WrappedComponent if authorized, or a fallback otherwise.
 */
const withSOSAuth = (WrappedComponent) => {
  return (props) => {
    // Authentication status: null = pending, or a string from env indicating auth result
    const [isAuthenticated, setIsAuthenticated] = useState(null);

    // Ref to store menu items to avoid unnecessary re-renders
    const menuItemsRef = useRef([]);

    // Access authorization context and current route location
    const { authorizationContext } = useContext(AuthContext);
    const location = useLocation();

    /**
     * Effect that runs when the authorization context or the current path changes.
     * It checks if the current path is included in the list of authorized menu paths.
     */
    useEffect(() => {
      if (authorizationContext?.menus.length > 0) {
        // Store menu items in ref
        menuItemsRef.current = authorizationContext.menus;

        // Collect all authorized paths (including children)
        const authorizedPaths = [];
        const collectPaths = (items) => {
          items.forEach((item) => {
            authorizedPaths.push(item.path);
            if (item.children && item.children.length > 0) {
              collectPaths(item.children);
            }
          });
        };
        collectPaths(menuItemsRef.current);

        // Check if current pathname matches or starts with any authorized path
        const currentPath = location.pathname;
        const isAuthorized = authorizedPaths.some(
          (authorizedPath) =>
            currentPath === authorizedPath ||
            currentPath.startsWith(authorizedPath + '/'),
        );

        if (isAuthorized) {
          setIsAuthenticated(import.meta.env.VITE_AUTH_AUTHORIZED);
        } else {
          setIsAuthenticated(import.meta.env.VITE_AUTH_UNAUTHORIZED);
        }
      }
    }, [authorizationContext, location.pathname]);

    // Show loader while the authentication status is being determined
    if (isAuthenticated === null) {
      return <Loader />;
    }

    // If unauthorized, show the "user not found" or "no access" component
    if (isAuthenticated === import.meta.env.VITE_AUTH_UNAUTHORIZED) {
      return <AccessDenied />;
    }

    // If authorized, render the original wrapped component with all props
    return <WrappedComponent {...props} />;
  };
};

export default withSOSAuth;
